import React, { useState } from 'react';
import { CheckCircle, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const CalculatePage = () => {
  // Personal Information
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  
  // Calculator Fields
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [activityLevel, setActivityLevel] = useState('');
  const [bodyFat, setBodyFat] = useState('');
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [utmParams, setUtmParams] = useState({
    utm_source: 'N/A',
    utm_medium: 'N/A',
    utm_campaign: 'N/A',
    utm_term: 'N/A',
  });

  // Capture UTM parameters from URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      utm_source: urlParams.get('utm_source') || 'N/A',
      utm_medium: urlParams.get('utm_medium') || 'N/A',
      utm_campaign: urlParams.get('utm_campaign') || 'N/A',
      utm_term: urlParams.get('utm_term') || 'N/A',
    };
    setUtmParams(params);
  }, []);

  // Don't fix viewport height for calculator - we want scrolling enabled

  const activityLevels = [
    { value: 'sedentary', label: 'Sjedjelački (kancelarijski posao, malo ili nimalo fizičke aktivnosti)' },
    { value: 'light', label: 'Umjereno aktivan (lagana fizička aktivnost 1–3 puta sedmično)' },
    { value: 'moderate', label: 'Srednje aktivan (umjerena fizička aktivnost 3–5 puta sedmično)' },
    { value: 'very', label: 'Veoma aktivan (intenzivan trening 6–7 puta sedmično)' },
    { value: 'extra', label: 'Izuzetno aktivan (veoma težak trening ili fizički posao)' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation - Personal Info
    if (!firstName.trim()) {
      toast.error('Molimo unesite vaše ime');
      return;
    }

    if (!lastName.trim()) {
      toast.error('Molimo unesite vaše prezime');
      return;
    }

    if (!email) {
      toast.error('Molimo unesite vaš email');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Molimo unesite validnu email adresu');
      return;
    }

    // Gender validation
    if (!gender) {
      toast.error('Molimo izaberite pol');
      return;
    }

    // Calculator fields validation
    if (!age || age <= 0) {
      toast.error('Molimo unesite validnu starost');
      return;
    }

    if (!weight || weight <= 0) {
      toast.error('Molimo unesite validnu težinu');
      return;
    }

    if (!height || height <= 0) {
      toast.error('Molimo unesite validnu visinu');
      return;
    }

    // Activity level validation
    if (!activityLevel) {
      toast.error('Molimo izaberite nivo aktivnosti');
      return;
    }

    // Push to Data Layer BEFORE any API calls
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'lead_submit',
        form_name: 'Balsa – TDEE Calculator'
      });
    }

    // Prepare data for webhook
    setIsSubmitting(true);
    
    // Get current date and time
    const now = new Date();
    const date = `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`;
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    // Get activity level label
    const activityLabel = activityLevels.find(a => a.value === activityLevel)?.label || activityLevel;

    const webhookData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      gender: gender,
      age: parseInt(age),
      weight: parseFloat(weight),
      height: parseFloat(height),
      activityLevel: activityLabel,
      bodyFat: bodyFat ? parseFloat(bodyFat) : null,
      date: date,
      time: time,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
    };

    try {
      // Send to Make.com webhook
      await axios.post('https://hook.eu2.make.com/7e5v77xe2vncc9mvv7ftyfusp8yc9pla', webhookData);
      
      // Push submittedForm event to Data Layer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'submittedForm'
      });
      
      // Show success message and update UI
      toast.success('Hvala vam! Javićemo se uskoro.');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Webhook error:', error);
      toast.error('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-container calculator-page">
      {/* Mobile Image - Shows first on mobile */}
      <div className="landing-image mobile-only">
        <img
          src="https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/66d21fc9-dfcc-4bdd-1151-b0072a2a7d00/public"
          alt="Fitness"
          className="landing-img-mobile"
        />
      </div>

      {/* Left Section - Form Content */}
      <div className="landing-left">
        <div className="landing-content">
          {!isSubmitted ? (
            <>
              <h1 className="landing-title calculator-title">
                Izračunajte vaše potrebe za kalorijama
              </h1>
              <p className="landing-subtitle">
                Popunite podatke i saznajte koliko kalorija vam je potrebno za postizanje vašeg cilja.
              </p>

              <form id="calculateForm" onSubmit={handleSubmit} className="landing-form calculator-form">
                {/* Personal Information Section */}
                <div className="form-section">
                  <div className="form-row">
                    <Input
                      type="text"
                      placeholder="Ime"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="landing-input"
                      required
                    />
                    <Input
                      type="text"
                      placeholder="Prezime"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="landing-input"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <Input
                      type="email"
                      placeholder="Email adresa"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="landing-input"
                      required
                    />
                  </div>
                </div>

                {/* Calculator Section */}
                <div className="form-section">
                  {/* Gender and Age in one row */}
                  <div className="form-row-mixed">
                    <div className="gender-section-inline">
                      <label className="field-label">Pol</label>
                      <div className="gender-options">
                        <button
                          type="button"
                          onClick={() => setGender('male')}
                          className={`gender-button ${gender === 'male' ? 'selected' : ''}`}
                        >
                          Muško
                        </button>
                        <button
                          type="button"
                          onClick={() => setGender('female')}
                          className={`gender-button ${gender === 'female' ? 'selected' : ''}`}
                        >
                          Žensko
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="field-label">Starost</label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="25"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        className="landing-input"
                        min="1"
                        max="120"
                        required
                      />
                    </div>
                  </div>

                  {/* Weight and Height */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="field-label">Težina (kg)</label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        placeholder="70"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        className="landing-input"
                        step="0.1"
                        min="1"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="field-label">Visina (cm)</label>
                      <Input
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="175"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                        className="landing-input"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  {/* Activity Level and Body Fat in one row */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="field-label">Nivo aktivnosti</label>
                      <select
                        value={activityLevel}
                        onChange={(e) => setActivityLevel(e.target.value)}
                        className="landing-select"
                        required
                      >
                        <option value="">Izaberite nivo aktivnosti</option>
                        {activityLevels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="field-label">Procenat masti (opciono)</label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        placeholder="15"
                        value={bodyFat}
                        onChange={(e) => setBodyFat(e.target.value)}
                        className="landing-input"
                        step="0.1"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="landing-submit calculator-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Šaljem...' : 'Izračunaj moje potrebe'}
                </Button>
              </form>
            </>
          ) : (
            <div className="thank-you-container">
              <h1 className="landing-title">Hvala vam!</h1>
              <p className="landing-subtitle">
                Molimo provjerite vaš inbox (provjerite i Spam) za vaše personalizovane rezultate.
              </p>
              <CheckCircle className="thank-you-icon" size={120} />
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Image (Desktop only) */}
      <div className="landing-right desktop-only">
        <img
          src="https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/d70b7c22-03da-4b9a-ef96-f73e658f2200/public"
          alt="Fitness"
          className="landing-img"
        />
      </div>
    </div>
  );
};

export default CalculatePage;
